import React, { ReactNode } from 'react';
import { Button, Card, MenuItem, Select, CircularProgress, Checkbox, Input } from '@mui/material';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { getAccounts } from '../../../sp-firebase/accountFB';
import BasicTable from '../../FinancialPositions/components/BasicTable';
import { AdminLoading } from './AdminCommon';
import { genericFormatter, getColDefs } from '../../FinancialPositions/components/BasicTableUtils';
import { getAllInProcess } from '../../Dashboard/dashboard-components/DashboardNotifications/notificationUtils';
import { Link } from 'react-router-dom';
import { StatusType } from '../../../sp-types/statusType';
import { DashboardNotification } from '../../../sp-types/notificationType';
import { Timestamp } from 'firebase/firestore';
import { getFileByUrl } from '../../../sp-firebase/storageFB';
import { ApprovalOutlined, Delete, OpenInNew } from '@mui/icons-material';
import { deleteFutureOrder } from '@sp-firebase/futureOrderFB';
import { deleteProduct } from '@sp-firebase/productFB';
import { deleteBalanceFlow } from '@sp-firebase/balanceFlowFB';
import { deleteInventoryReportId, setInventoryReportById } from '@sp-firebase/inventoryReportFB';
import { OverlayDisplayDiv } from '@UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { AddMaterialPopup } from '../../Inventory/inventory-components/AddMaterialPopup';
import { BatchFileEditor } from './BatchFileEditor';
import { ManageDocTypesPop } from './ManageDocTypesPop';

export const AdminProcesses: React.FC = () => {
  const { account } = useAccountStore();

  const { materials } = useMaterialsStore();
  const [accountId, setAccountId] = React.useState<string>('');
  const [accounts, setAccounts] = React.useState<string[] | undefined>();

  const [gridItems, setGridItems] = React.useState<any>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showPending, setShowPending] = React.useState<boolean>(true);
  const [showAddMaterial, setShowAddMaterial] = React.useState<string | undefined>();
  const [showDocTypesPop, setShowDocTypesPop] = React.useState<string | undefined>();

  const getAllAccounts = async () => {
    setLoading(true);
    const allAccounts = await getAccounts();

    setAccounts(allAccounts?.map(({ id }) => id || '') || []);
    setAccountId(allAccounts?.[0]?.id || account?.id || '');

    setLoading(false);
  };

  React.useEffect(() => {
    getAllAccounts();
  }, [account]);

  const fetchData = async () => {
    setLoading(true);
    const processes = await getAllInProcess(accountId, [
      'underwriting',
      'started',
      ...(showPending ? ['pending' as StatusType] : []),
    ]);
    console.log('processes', processes);

    setGridItems(processes.sort(sortByDateDESC) || [{ title: 'Data not found' }]);
    setLoading(false);
  };

  const sortByDateDESC = (a: DashboardNotification, b: DashboardNotification): number =>
    (b.date?.seconds || 0) - (a.date?.seconds || 0);
  const colDefs = { date: '', formulaId: '', text: '', actionUrl: '', type: '', status: '', zactions: '' };

  const downloadFile = async (fileKey: string) => {
    const fileUrl = await getFileByUrl(fileKey);
    if (fileUrl) {
      window.open(fileUrl, '_blank');
      console.debug('downloading file :', fileUrl);
    } else {
      console.debug('file not found. key:', fileKey);
    }
  };

  const downloadFileLink = (original?: any): ReactNode | undefined => (
    <>
      {original?.options?.fileUrl && (
        <Button variant="outlined" onClick={() => downloadFile(original.options.fileUrl)}>
          download file
          <OpenInNew />
        </Button>
      )}
      {original?.options?.files && original?.options?.isFileRecord && (
        <BatchFileEditor accountId={accountId} options={original?.options} />
      )}
    </>
  );

  const updateInventoryReport = async (formulaId: string, quantity: number, reportDate: Date) => {
    if (showAddMaterial) {
      await setInventoryReportById(showAddMaterial, {
        accountId,
        formulaId,
        quantity,
        reportingTime: Timestamp.fromDate(reportDate),
        monthYear: { month: reportDate.getMonth(), year: reportDate.getFullYear() },
        status: 'completed',
      } as any);

      setShowAddMaterial(undefined);
      fetchData();
    }
  };

  const deleteItem = async (original: any) => {
    if (original?.id && original.options?.itemType === 'futureOrder') {
      await deleteFutureOrder(original.id);

      fetchData();
    }
    if (original?.id && original.options?.itemType === 'inventory') {
      await deleteInventoryReportId(original.id);

      fetchData();
    }
    if (original?.id && original.options?.itemType === 'balanceFlow') {
      await deleteBalanceFlow(original.id);

      fetchData();
    }
    if (original?.id && original.options?.itemType === 'product') {
      await deleteProduct(original.id);

      fetchData();
    }
  };

  const customFormatter = (data: any): ReactNode | undefined => {
    if (data?.column.Header === 'actionUrl') {
      return (
        <div className="flex justify-between">
          {data?.value ? (
            <Link className="text-blue-600" to={data?.value + '?fromAdmin=true'}>
              View page
            </Link>
          ) : (
            <span> - </span>
          )}
          {downloadFileLink(data?.row.original)}
        </div>
      );
    }
    if (data?.column.Header === 'Actions' && data?.row.original.options?.itemType === 'inventory') {
      return (
        <div className="flex justify-between w-fit">
          <Button onClick={() => deleteItem(data?.row.original)}>
            <Delete />
          </Button>
          <Button onClick={() => setShowAddMaterial(data?.row.original?.id)}>
            <ApprovalOutlined />
          </Button>
        </div>
      );
    }
    if (data?.column.Header === 'Actions' && data?.row.original.options?.itemType) {
      return (
        <div className="flex justify-between">
          <Button onClick={() => deleteItem(data?.row.original)}>
            <Delete />
          </Button>
        </div>
      );
    }
    if (data?.value instanceof Timestamp) {
      const value = data?.value as Timestamp;
      return <span>{value.toDate().toLocaleString('en-CA', { hour12: false })}</span>;
    }
    return genericFormatter(data);
  };

  return accounts ? (
    <div className="w-full h-full bg-white flex flex-col">
      {showAddMaterial && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowAddMaterial(undefined)} hideClose>
          <AddMaterialPopup
            isUpdate
            materials={materials}
            cancel={() => setShowAddMaterial(undefined)}
            onInvAdded={() => {}}
            addMaterialToInventory={updateInventoryReport}
          />
        </OverlayDisplayDiv>
      )}
      <Card className="flex flex-row gap-4 align-top h-12 m-1 p-2 shadow-custom ">
        Account:
        <Select label="Account" onChange={({ target }) => setAccountId(target?.value || '')} value={accountId}>
          {accounts?.map((account, i) => (
            <MenuItem key={i} id={account} value={account}>
              {account}
            </MenuItem>
          ))}
        </Select>
        <div className="flex flex-row">
          <label className="justify-center items-center ">Show pending</label>
          <Checkbox onChange={() => setShowPending(!showPending)} checked={showPending} title="Show pending" />
        </div>
        <Button onClick={fetchData} variant="contained">
          Get data
          {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
        </Button>
        <Button onClick={() => setShowDocTypesPop(accountId)} variant="contained">
          Manage Document types
          {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
        </Button>
      </Card>
      {showDocTypesPop && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowDocTypesPop(undefined)}>
          <ManageDocTypesPop accountId={accountId} />
        </OverlayDisplayDiv>
      )}
      {gridItems && (
        <div className="flex h-[90%] overflow-auto">
          <BasicTable
            allRows={gridItems}
            columns={getColDefs(colDefs, customFormatter)
              .filter((c) => c.name !== 'ID')
              .map((a) => (a.name === 'zactions' ? { ...a, name: 'Actions' } : a))}
            options={{ cellClass: 'max-h-24', noBorders: true, filters: { status: true, text: true } }}
          ></BasicTable>
        </div>
      )}
    </div>
  ) : (
    <AdminLoading />
  );
};
